// A Radar showing a team, or teams compared (?live=1; teams and matches, Ben 2026-10-30 — docs/
// modules/teams-and-matches.md "5. Radar": "I don't want a 'player radar' AND a 'team radar'. I
// want 'radar'"). Drop a team on it, or pick one in its search. Two templates: Attacking (its own
// numbers) and Defending (its opponents', turned round so a bigger shape = better defending). Each
// spoke runs from the lowest to the highest team in the leagues on it, that season (live/
// team-radar.ts). Drop more teams on it to compare them, up to five, each in its colour, as a Stats
// tile compares teams (canvas/team-merge.ts). The player Radar's two buttons too (Ben, 2026-11-02):
// Distribution (each spoke's spread across those teams, the team(s) marked) and the spoke editor
// (any team number, kept per template; Reset brings back the ten). No league average shape (Ben);
// the spokes don't drag out yet (no team Ranking to drag them to).

import { Suspense, useEffect, useReducer, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { TeamStatsResponse } from "@catbento/shared";
import { getModState, setModState } from "./bridge.js";
import { SubControlsSlot } from "./SubControlsSlot.js";
import { StyledSelect } from "./StyledSelect.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainTeamRadar, listWords } from "./explain.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import { CHART_SKELETON_HTML, ensureChartFrame, setAxisTicksActive, teardownRadarChart, updateRadarData, type RadarEntry } from "./radar-chart.js";
import { polygonPoints } from "./radar-view.js";
import { AxisEditor, RadarEditAxesButton } from "./RadarAxisEditor.js";
import { RadarDistribution } from "./RadarDistribution.js";
import { RadarDistributionOverlay } from "./RadarDistributionOverlay.js";
import { formatMetricValue } from "../engines/stats-filter-kit.js";
import { useLiveLeagueChoices } from "../hooks/useLeagueTable.js";
import { useLiveQueries } from "../live/query.js";
import { leagueSeasonsQuery, teamStatsQuery } from "../live/team-stats.js";
import {
  TEAM_RADAR_TEMPLATES,
  TEAM_STAT_CATEGORIES,
  defaultTeamSpokes,
  spokeCategories,
  teamEditorGroups,
  teamRadarAxes,
  teamRadarFractions,
  teamRadarSpokes,
  teamRadarTemplate,
  teamRadarValues,
  teamSpread,
  type TeamRadarTemplate,
} from "../live/team-radar.js";
import type { TeamRef } from "../live/team.js";
import { canvasApi } from "../canvas/shell-actions.js";
import { undoApi } from "../canvas/undo.js";
import { startTeamTokenDrag } from "../canvas/menu-drag.js";
import { mergedTeamsOf, unmergeTeam } from "../canvas/team-merge.js";
import { COMPARE_LETTERS } from "../canvas/drop-rules.js";
import { hideTooltip, showTooltipAt } from "../canvas/tooltip.js";

export function TeamRadar({ mod, body, team }: { mod: HTMLElement; body: HTMLElement; team: TeamRef }) {
  const [, forceRender] = useReducer((x: number) => x + 1, 0);
  const choices = useLiveLeagueChoices();
  const teams = mergedTeamsOf(mod) ?? [team];
  const comparing = teams.length > 1;
  const colors = comparing ? (getModState<string[]>(mod, "_mergedTeamColors") ?? [...COMPARE_LETTERS]) : ["a"];
  const template = teamRadarTemplate(getModState(mod, "_teamRadarTemplate"));
  const storedSpokes = getModState<Partial<Record<TeamRadarTemplate, string[]>>>(mod, "_teamRadarSpokes");
  const spokes = teamRadarSpokes(template, storedSpokes);
  const cats = spokeCategories(spokes);
  const isDist = getModState<string>(mod, "_radarView") === "distribution";
  const [editorOpen, setEditorOpen] = useState(false);
  const editBtnRef = useRef<HTMLButtonElement>(null);

  // Each team's season: the one it was picked up in, else its league's newest. The lone team's
  // league seasons also fill the season dropdown.
  const leagueIds = [...new Set(teams.map((t) => t.competitionId))];
  const leagueSeasons = useLiveQueries(leagueIds.map(leagueSeasonsQuery));
  const seasonsOf = (t: TeamRef) => leagueSeasons[leagueIds.indexOf(t.competitionId)] ?? [];
  const seasons = teams.map((t) => t.season ?? seasonsOf(t)[0] ?? null);
  // One answer per league season and category the spokes need, as the Stats tiles ask.
  const answers = useLiveQueries<TeamStatsResponse | null>(
    teams.flatMap((t, i) =>
      cats.map((cat) => {
        const season = seasons[i] ?? null;
        return season === null
          ? { key: `team-stats:${t.competitionId}:none:${cat}`, load: (): Promise<TeamStatsResponse | null> => Promise.resolve(null) }
          : teamStatsQuery(t.competitionId, season, cat);
      }),
    ),
  );
  const answersOf = (i: number) => answers.slice(i * cats.length, (i + 1) * cats.length);

  const axes = teamRadarAxes(spokes, answers);
  const valuesList = teams.map((t, i) => teamRadarValues(axes, answersOf(i), t.id));
  const missing = teams.filter((_, i) => valuesList[i]!.every((v) => v === null));
  const leagueName = (t: TeamRef) => choices.find((c) => c.id === t.competitionId)?.label ?? "league";
  const seasonText = (i: number) => (seasons[i] ? humanizeSeasonCode(seasons[i] as string) : "");

  const entries: RadarEntry[] = teams.map((t, i) => ({
    colorLetter: colors[i] ?? "a",
    fractions: teamRadarFractions(axes, valuesList[i]!),
    values: valuesList[i]!,
    tooltipPrefix: comparing ? t.name : seasonText(i),
  }));
  // Stand-ins for `axes` and `entries` (fresh arrays each render) in the chart's effect.
  const axesKey = JSON.stringify(axes);
  const entriesKey = JSON.stringify(entries);
  const hasChart = axes.length > 0 && missing.length < teams.length;

  // The header: across a comparison there's no one team to search for (as for players).
  useEffect(() => {
    body.style.justifyContent = "flex-start";
    if (!comparing) return;
    const search = mod.querySelector<HTMLElement>(":scope > .module-sub-controls .player-search-wrap");
    if (!search) return;
    const prev = search.style.display;
    search.style.display = "none";
    return () => {
      search.style.display = prev;
    };
  }, [mod, body, comparing]);

  // The wheel, an imperative island (as the player Radar's): built once a chart can show, torn
  // down with it.
  const svgWrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const wrap = svgWrapRef.current;
    if (!wrap || !hasChart) return;
    wrap.innerHTML = CHART_SKELETON_HTML;
    const svg = wrap.querySelector<SVGSVGElement>(".radar-svg");
    return () => {
      if (svg) teardownRadarChart(svg);
      wrap.innerHTML = "";
    };
  }, [hasChart]);
  useEffect(() => {
    const svg = svgWrapRef.current?.querySelector<SVGSVGElement>(".radar-svg");
    if (!svg || !hasChart) return;
    ensureChartFrame(svg, axes);
    updateRadarData(svg, axes, entries);
  }, [axesKey, entriesKey, hasChart]);

  const onToggleView = () => {
    setModState(mod, "_radarView", isDist ? "radar" : "distribution");
    forceRender();
  };
  // The editor's picks, per template; undefined = the template's ten.
  const persistSpokes = (next: string[] | undefined) => {
    setModState(mod, "_teamRadarSpokes", { ...storedSpokes, [template]: next });
    forceRender();
  };
  const onTemplateChange = (next: string) => {
    // The editor shows one template's spokes.
    setEditorOpen(false);
    setModState(mod, "_teamRadarTemplate", next === "defending" ? "defending" : undefined);
    forceRender();
  };
  // The lone team's season. Another team keeps the template.
  const onSeasonChange = (code: string) => {
    if (code === seasons[0]) return;
    undoApi().saveState();
    setModState(mod, "_team", { ...team, season: code });
    canvasApi().refreshModuleBody(mod);
  };
  const onChipDrag = (e: ReactPointerEvent<HTMLElement>, t: TeamRef, season: string | null) => {
    if (window.innerWidth <= 768 || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    startTeamTokenDrag(e.nativeEvent, { ...t, season });
  };

  // A hovered chip's team shows as its own radar (its banded fill, via the shared clip re-pointed
  // at its shape) while the others dim, as on the player Radar's comparison.
  const onLegendEnter = (i: number) => {
    const svg = svgWrapRef.current?.querySelector<SVGSVGElement>(".radar-svg");
    const polyG = svg?.querySelector<SVGGElement>(".radar-polygons");
    const bandedFillG = svg?.querySelector<SVGGElement>(".radar-banded-fill");
    const clipPolygon = svg?.querySelector<SVGPolygonElement>(".radar-clip-polygon");
    if (!polyG || !bandedFillG || !clipPolygon) return;
    const target = polyG.querySelector(`.radar-polygon.marker-${entries[i]!.colorLetter}`);
    Array.from(polyG.children).forEach((poly) => {
      poly.classList.toggle("compare-highlighted", poly === target);
      poly.classList.toggle("compare-dimmed", poly !== target);
    });
    clipPolygon.setAttribute("points", polygonPoints(axes, entries[i]!.fractions));
    polyG.parentNode?.insertBefore(bandedFillG, polyG.nextSibling); // raise the banded fill above the dimmed shapes
  };
  const onLegendLeave = () => {
    const svg = svgWrapRef.current?.querySelector<SVGSVGElement>(".radar-svg");
    const polyG = svg?.querySelector<SVGGElement>(".radar-polygons");
    const bandedFillG = svg?.querySelector<SVGGElement>(".radar-banded-fill");
    const clipPolygon = svg?.querySelector<SVGPolygonElement>(".radar-clip-polygon");
    if (!polyG || !bandedFillG || !clipPolygon) return;
    Array.from(polyG.children).forEach((poly) => poly.classList.remove("compare-highlighted", "compare-dimmed"));
    clipPolygon.setAttribute("points", "");
    polyG.parentNode?.insertBefore(bandedFillG, polyG); // back to its fixed spot before the polygons
  };

  // A table row lights its wedge, as on the player Radar.
  const wedgeFor = (key: string): SVGElement | null => {
    const svg = svgWrapRef.current?.querySelector<SVGSVGElement>(".radar-svg");
    return svg ? ([...svg.querySelectorAll<SVGElement>(".radar-wedge")].find((w) => w.dataset.axisKey === key) ?? null) : null;
  };
  const onRowEnter = (key: string) => {
    const svg = svgWrapRef.current?.querySelector<SVGSVGElement>(".radar-svg");
    const wedge = wedgeFor(key);
    if (!svg || !wedge) return;
    wedge.classList.add("row-hover");
    setAxisTicksActive(svg, key, true);
    if (wedge.dataset.tooltip) {
      const rect = wedge.getBoundingClientRect();
      showTooltipAt(wedge.dataset.tooltip, rect.left + rect.width / 2, rect.top + rect.height / 2);
    }
  };
  const onRowLeave = (key: string) => {
    const svg = svgWrapRef.current?.querySelector<SVGSVGElement>(".radar-svg");
    const wedge = wedgeFor(key);
    if (svg && wedge) {
      wedge.classList.remove("row-hover");
      setAxisTicksActive(svg, key, false);
    }
    hideTooltip();
  };

  const templateSelect = (
    <StyledSelect
      items={TEAM_RADAR_TEMPLATES.map((t) => ({ value: t.value, label: t.label }))}
      value={template}
      onChange={onTemplateChange}
      wrapClass="template-select-wrap"
      extraClass="template-select"
    />
  );
  const leagues = [...new Set(teams.map(leagueName))];
  // Distribution: each spoke's spread across the teams the wheel is scaled to.
  const spread = teamSpread(answers, leagues.length === 1 ? `${leagues[0]} teams` : `${leagues.length} leagues' teams`);
  const valueMaps = valuesList.map((vals) => Object.fromEntries(axes.map((a, k) => [a.key, vals[k] ?? null])));
  const empty = hasChart
    ? null
    : `No team stats for ${listWords(teams.map((t, i) => `${t.name} in the ${leagueName(t)} ${seasonText(i) || "this season"}`))} yet.`;
  // A team that has nothing while the others do: said, never a shape at the middle.
  const note = hasChart && missing.length ? `No team stats yet for ${listWords(missing.map((t) => t.name))}.` : null;

  return (
    <>
      <ModuleExplainer
        mod={mod}
        paragraphs={explainTeamRadar({
          template: template === "defending" ? "Defending" : "Attacking",
          teams: teams.map((t, i) => ({ name: t.name, league: leagueName(t), season: seasonText(i) })),
          leagues: leagues.length,
          distribution: isDist,
          edited: storedSpokes?.[template] !== undefined,
        })}
      />
      <SubControlsSlot mod={mod}>
        {!comparing && (
          <StyledSelect
            items={seasonsOf(team).map((c) => ({ value: c, label: humanizeSeasonCode(c) }))}
            value={seasons[0] ?? ""}
            onChange={onSeasonChange}
            wrapClass="season-select-wrap"
            extraClass="season-select"
          />
        )}
        {templateSelect}
        {/* As on the player Radar: the button names the view a click switches TO. */}
        <button type="button" className="module-switch-btn radar-view-toggle" onClick={onToggleView}>
          {isDist ? "Radar" : "Distribution"}
        </button>
        <RadarEditAxesButton open={editorOpen} btnRef={editBtnRef} onToggle={() => setEditorOpen((o) => !o)} />
      </SubControlsSlot>
      {editorOpen && editBtnRef.current && (
        // Its own wait: the list's categories load without the Radar going to "Loading".
        <Suspense fallback={null}>
          <TeamSpokeEditor
            anchor={editBtnRef.current}
            team={teams[0]!}
            season={seasons[0] ?? null}
            template={template}
            spokes={spokes}
            labelOf={(spoke) => axes.find((a) => a.key === spoke.slice(spoke.indexOf(":") + 1))?.label ?? spoke}
            onPersist={persistSpokes}
            onClose={() => setEditorOpen(false)}
          />
        </Suspense>
      )}
      <div className={comparing ? "compare-wrap" : "radar-wrap"}>
        {comparing && (
          <div className="compare-legend">
            {teams.map((t, i) => (
              <span
                key={i}
                className={`compare-chip compare-chip-${colors[i]}`}
                onPointerDown={(e) => {
                  if ((e.target as HTMLElement).closest(".compare-remove")) return;
                  onChipDrag(e, t, seasons[i] ?? null);
                }}
                onMouseEnter={() => onLegendEnter(i)}
                onMouseLeave={onLegendLeave}
              >
                <span className="compare-dot" />
                {t.name} <span className="compare-chip-season">({seasonText(i) || "—"})</span>
                <span className="compare-remove" onClick={() => unmergeTeam(mod, i)}>
                  ✕
                </span>
              </span>
            ))}
          </div>
        )}
        {note && <div className="radar-shape-note">{note}</div>}
        {empty !== null ? (
          <div className="stats-empty">{empty}</div>
        ) : (
          <div className="radar-body-layout">
            {/* The wheel stays mounted under Distribution, as on the player Radar. */}
            <div className="radar-svg-wrap" ref={svgWrapRef} style={isDist ? { display: "none" } : undefined} />
            <div className="radar-table-wrap">
              {isDist ? (
                comparing ? (
                  <RadarDistributionOverlay spread={spread} axes={axes} names={teams.map((t) => t.name)} valuesList={valueMaps} letters={colors} />
                ) : (
                  <RadarDistribution spread={spread} axes={axes} values={valueMaps[0]!} />
                )
              ) : (
              <table className="radar-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Min</th>
                    {comparing ? (
                      teams.map((t, i) => (
                        <th key={i}>
                          <span className={`marker-${colors[i]} radar-th-name`}>{t.name}</span>
                        </th>
                      ))
                    ) : (
                      <th>Value</th>
                    )}
                    <th>Max</th>
                  </tr>
                </thead>
                <tbody>
                  {axes.map((axis, a) => (
                    <tr key={axis.key} data-axis-key={axis.key} onMouseEnter={() => onRowEnter(axis.key)} onMouseLeave={() => onRowLeave(axis.key)}>
                      <td className="radar-table-metric">{axis.label}</td>
                      <td>{formatMetricValue(axis.min, axis.outputType)}</td>
                      {teams.map((_, i) => (
                        <td key={i} className={`radar-table-value${comparing ? ` marker-${colors[i]}` : ""}`}>
                          {formatMetricValue(valuesList[i]![a], axis.outputType)}
                        </td>
                      ))}
                      <td>{formatMetricValue(axis.max, axis.outputType)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}


/** The spoke editor for a team Radar: the player Radar's popover over the engine's team numbers
 *  (the first team's league season names them; every team has the same list). */
function TeamSpokeEditor(x: {
  anchor: HTMLElement;
  team: TeamRef;
  season: string | null;
  template: TeamRadarTemplate;
  spokes: readonly string[];
  labelOf: (spoke: string) => string;
  onPersist: (next: string[] | undefined) => void;
  onClose: () => void;
}) {
  const answers = useLiveQueries<TeamStatsResponse | null>(
    TEAM_STAT_CATEGORIES.map((c) =>
      x.season === null
        ? { key: `team-stats:${x.team.competitionId}:none:${c.id}`, load: (): Promise<TeamStatsResponse | null> => Promise.resolve(null) }
        : teamStatsQuery(x.team.competitionId, x.season, c.id),
    ),
  );
  const groups = teamEditorGroups(
    x.template,
    TEAM_STAT_CATEGORIES.map((c, i) => ({ ...c, answer: answers[i] ?? null })),
  );
  const names = new Map(groups.flatMap((g) => g.metrics).map((m) => [m.key, m.shortName || m.name]));
  return (
    <AxisEditor
      anchor={x.anchor}
      initialKeys={x.spokes}
      groups={groups}
      labelOf={(spoke) => names.get(spoke) ?? x.labelOf(spoke)}
      onPersist={(next) => x.onPersist(next)}
      onReset={() => {
        x.onPersist(undefined);
        return defaultTeamSpokes(x.template);
      }}
      onClose={x.onClose}
    />
  );
}
